import React, { useEffect, useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { CardActions } from '@mui/material';
import { useSelector } from 'react-redux';
import ReactApexChart from 'react-apexcharts';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Timer from './Timer';

const EarningAnalysis = () => {
  const formatPrice = (amount)=> new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', currencySign: 'standard' }).format(amount);
  const investments = useSelector((state) => state.history.investments);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const ticker = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(ticker);
  }, []);

  const parseDurationToMs = (duration) => {
    if (!duration || typeof duration !== 'string') return 0;

    const lowerDuration = duration.toLowerCase().trim();
    const regex = /(\d+(?:\.\d+)?)\s*(year|years|month|months|week|weeks|day|days|hour|hours|minute|minutes|min|mins)/;
    const match = lowerDuration.match(regex);
    if (!match) return 0;

    const value = parseFloat(match[1]);
    const unit = match[2];

    if (unit.startsWith('year')) return value * 365 * 24 * 60 * 60 * 1000;
    if (unit.startsWith('month')) return value * 30 * 24 * 60 * 60 * 1000;
    if (unit.startsWith('week')) return value * 7 * 24 * 60 * 60 * 1000;
    if (unit.startsWith('day')) return value * 24 * 60 * 60 * 1000;
    if (unit.startsWith('hour')) return value * 60 * 60 * 1000;
    return value * 60 * 1000;
  };

  const getCountdownText = (remainingMs) => {
    if (remainingMs <= 0) return 'Matured';

    const totalSeconds = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (days > 0) return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    return `${hours}h ${minutes}m ${seconds}s`;
  };

  const activeInvestments = useMemo(
    () => (investments || []).filter((investment) => investment.status === 'approved'),
    [investments]
  );

  return (
    <div>
       {activeInvestments.length > 0 &&
        <Grid container px={2} mt={0}  mb={4} rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
      {activeInvestments.map(investment =>{
        const startTime = new Date(investment.created_at).getTime();
        const durationMs = parseDurationToMs(investment.duration);
        const elapsedMs = Math.max(0, now - startTime);
        const boundedElapsed = durationMs > 0 ? Math.min(elapsedMs, durationMs) : 0;
        const remainingMs = Math.max(0, durationMs - boundedElapsed);

        const elapsedPercent = durationMs > 0 ? (boundedElapsed / durationMs) * 100 : 0;
        const remainingPercent = Math.max(0, 100 - elapsedPercent);

        const pieSeries = durationMs > 0
          ? [elapsedPercent, remainingPercent]
          : [0, 100];

        const profit = Number(investment.earning || 0).toFixed(2);
        const countdownText = getCountdownText(remainingMs);

        return (
        <Grid key={investment.id} item xs={12} md={4}>
            <Card  sx={{marginTop: '0px', paddingX: '2px', marginBottom: '0px'}} elevation={2}>
           
            <CardContent sx={{marginBottom: '-30px'}}>
                <Typography gutterBottom variant="h5" component="div">
                AMOUNT: {formatPrice(investment.amount_invested)}
            </Typography>
            <Box sx={{display: 'flex', justifyContent: 'center'}}>
            <ReactApexChart options={{
              chart: {
                type: 'pie',
              },
              colors: ['#ff6600', '#bce70c'],
              legend: {
                show: false,
                showForSingleSeries: false,
                showForNullSeries: false,
                showForZeroSeries: false,
                position: 'middle'
              },
              
              labels: ['ELAPSED', 'REMAINING'],
              dataLabels: {
                offsetX: 0,
                offsetY: 0,
                textAnchor: 'start',
                formatter(val, opts) {
                    
                  const name = opts.w.globals.labels[opts.seriesIndex]
                  return [name, val.toFixed(4) + '%']
                  
                },
                
              },
              plotOptions: {
                pie: {
                  dataLabels: {
                    offset: -20
                  }
                }
              }
              ,
              responsive: [{
                breakpoint: 480,
                options: {
                  chart: {
                    width: 200
                  },
                  
                }
              }]
            }}
            series={pieSeries}
            type="pie" height={180}
            />
            </Box>
                        
            </CardContent>

            <CardActions sx={{display: 'flex', justifyContent: 'center'}}>
               <Timer profit={profit} countdownText={countdownText}/>
            </CardActions>

        </Card>
        </Grid> 
      )})
      
    }
    </Grid>
      
      }


    </div>
  )
}

export default EarningAnalysis
